/**
 * AdminCrossCheckFields — admin-only cross-check block appended to every
 * parsed-feedback schema view. Surfaces the values an admin needs to sanity-
 * check a run:
 *   - "Answers Detected" — student answer, correct answer (LLM), and the LLM's
 *     answer-match verdict, grouped in one sub-section.
 *   - "Reader Consistency" — how the two independent readings of the page
 *     compared, the analyser's own judgement. Shown whether or not it found a
 *     disagreement: a clean comparison is the evidence that the reading can be
 *     trusted, and it is only visible here.
 *   - "Confidence" — the LLM's self-reported confidence, colour-coded.
 *   - "Diagnosis and Learning Moments" — what the analyser concluded went
 *     wrong, and which remedies the detector then fired on this attempt. The
 *     two belong together: the diagnosis is the finding and the moments are what
 *     we did with it, and reading either alone leaves an admin unable to tell a
 *     bad diagnosis from a detector that ignored a good one. Headed by the
 *     attempt's working role (marked / not marked, and which layer said so),
 *     with any finding withheld because the working was not marked.
 *
 * Rendered with the shared `sol-section` / `sol-section-title` styling so the
 * block is visually consistent with the analysis sections above it.
 */
import type { Diagnosis, Feedback, WorkingRoleSource } from '@padcanvas/shared';
import { LatexText } from '../latex';
import { MomentRejectControl, rejectionNote } from '../learning-moments/MomentRejectControl';
import type { EncounterWithdrawn } from '../learning-moments/types';
import type { AdjustmentWhyValue } from '../components/AdjustmentWhyFields';

/** One remedy the detector fired on this submission. */
export interface SolutionLearningMoment {
    learningMomentId: string;
    name: string;
    applied: boolean;
    context?: string;
    /** Set when the analysis this fired on was withdrawn. Shown rather than
     *  hidden: the encounter is marked and not deleted precisely so somebody
     *  debugging a bad moment can see that it fired on a reading we later
     *  rejected. Practice selection already skips these. */
    withdrawn?: EncounterWithdrawn & { byName?: string };
}

/** Reject (`true`, optional comment) or restore (`false`) one moment on this
 *  submission. See moment-rejection-target.md. */
export type RejectMomentHandler = (learningMomentId: string, rejected: boolean, comment?: string, why?: AdjustmentWhyValue) => Promise<unknown>;

interface Props {
    feedback: Feedback;
    question?: { correctAnswer?: string };
    /** Moments detected on THIS submission. Absent on the surfaces that do not
     *  load them; the sub-section then shows the diagnosis alone. */
    learningMoments?: SolutionLearningMoment[];
    /** Offer Reject / Restore on each moment. Omitted ⇒ read-only. */
    onRejectMoment?: RejectMomentHandler;
}

/** The ObjectiveCorrectnessV1 schema emits confidence as a 0–1 number while
 *  other prompts use a categorical label. Normalise to a display string plus
 *  a colour band: green > 0.8, yellow 0.5–0.8, red < 0.5 (high/medium/low map
 *  to the same bands). */
function resolveConfidence(c: unknown): { text: string; band: 'conf-high' | 'conf-mid' | 'conf-low' } | null {
    if (c === undefined || c === null || c === '') return null;
    if (typeof c === 'number') {
        const band = c > 0.8 ? 'conf-high' : c >= 0.5 ? 'conf-mid' : 'conf-low';
        return { text: c.toFixed(2), band };
    }
    const label = String(c).toLowerCase();
    const band = label === 'high' ? 'conf-high' : label === 'medium' ? 'conf-mid' : 'conf-low';
    return { text: String(c), band };
}

/** Which layer decided the working role, in the words an admin reads it in. */
const WORKING_SOURCE_LABEL: Record<WorkingRoleSource, string> = {
    session: 'set on the paper or assignment',
    tenant: 'tenant setting',
    platform: 'platform default',
    default: 'default',
};

/** Colour by what the verdict COSTS, not by whether the readers agreed:
 *  `absent` is red because neither reader found an answer and the score is
 *  almost certainly wrong, while `one_sided` is only worth a look. */
const READER_VERDICT_BAND: Record<string, 'conf-high' | 'conf-mid' | 'conf-low'> = {
    consistent: 'conf-high',
    one_sided: 'conf-mid',
    inconsistent: 'conf-low',
    absent: 'conf-low',
};

/** One row of the reader comparison. The note carries the whole finding
 *  ("split says ML^2/9, extraction says 2mL^2/9") so it is rendered beside the
 *  verdict rather than behind a hover. */
function ReaderRow({ label, judgement }: {
    label: string;
    judgement: { verdict: string; confidence: number; note: string };
}) {
    return (
        <>
            <dt>{label}</dt>
            <dd>
                <span className={`confidence-badge ${READER_VERDICT_BAND[judgement.verdict] ?? 'conf-mid'}`}>
                    {judgement.verdict.replace(/_/g, ' ')}
                </span>
                {judgement.note && <span className="reader-consistency-note"><LatexText text={judgement.note} /></span>}
            </dd>
        </>
    );
}

export function AdminCrossCheckFields({ feedback, question, learningMoments, onRejectMoment }: Props) {
    const a = feedback.llmAssessment;
    const studentAnswer = a?.studentLlmAnswer;
    const correctAnswer = question?.correctAnswer;
    const answerMatch = a?.answerMatch?.equivalent;
    const confidence = resolveConfidence(feedback.confidence);

    // `kind: 'none'` is the analyser saying there is nothing to diagnose, which
    // is a real answer and not an empty field — but it comes with no cause, so
    // there is nothing to render either.
    const d = a?.diagnosis;
    // A v2 `none` is a finding ("nothing to note"), shown so an admin can tell
    // it from an analyser that said nothing.
    const hasDiagnosis = !!(d && (d.cause || (d.kind && d.kind !== 'none') || d.concept || d.conceptName || d.category
        || (d.kind === 'none' && d.vocabularyVersion === 2)));
    const secondaries = a?.secondaryDiagnoses ?? [];
    const moments = learningMoments ?? [];
    // A withdrawn encounter is still shown — it is evidence about the detector —
    // but it is not a standing finding about the student, so the two are
    // counted apart.
    const standing = moments.filter(m => !m.withdrawn);
    const withdrawnCount = moments.length - standing.length;
    // The working role decides what a finding on a right answer is worth, so
    // it heads the diagnosis; what it held back is listed for the reviewer.
    const working = a?.workingRole;
    const withheld = a?.withheldForScratch ?? [];
    const hasRemedies = hasDiagnosis || moments.length > 0 || !!working || withheld.length > 0;

    const selection = a?.studentSelection;
    const hasAnswers = !!(studentAnswer || correctAnswer || answerMatch || (selection && selection.source !== 'none'));

    // Three states an admin needs to tell apart, and absence of the section is
    // the third: the comparison ran, only one reading existed so there was
    // nothing to compare, or the prompt never asked (older rows, and every
    // prompt not handed both readings).
    const readers = a?.readerConsistency;
    const readersCompared = !!readers
        && (readers.answer.verdict !== 'not_applicable' || readers.steps.verdict !== 'not_applicable');

    if (!hasAnswers && !confidence && !hasRemedies && !readers) return null;

    return (
        <>
            {hasAnswers && (
                <section className="sol-section sol-correct-llm-answer">
                    <h3 className="sol-section-title">Answers Detected</h3>
                    <dl className="admin-answers">
                        {studentAnswer && (
                            <>
                                <dt>Student Answer</dt>
                                <dd><LatexText text={studentAnswer} /></dd>
                            </>
                        )}
                        {/* The same answer as option labels, and how the student
                            gave it — what the MCQ scorers act on when nothing
                            was ticked. Shown only on an MCQ that reported one. */}
                        {selection && selection.source !== 'none' && (
                            <>
                                <dt>Options chosen</dt>
                                <dd>
                                    {selection.options.join(', ') || 'none'}
                                    {' — '}{selection.source === 'marked' ? 'marked on the page' : 'matched from the written value'}
                                    {selection.clarity === 'ambiguous' && <strong> · not clearly readable</strong>}
                                    {selection.evidence && <> · <LatexText text={selection.evidence} /></>}
                                </dd>
                            </>
                        )}
                        {correctAnswer && (
                            <>
                                <dt>Correct Answer (LLM)</dt>
                                <dd><LatexText text={correctAnswer} /></dd>
                            </>
                        )}
                        {answerMatch && (
                            <>
                                <dt>Answer Match</dt>
                                <dd>{answerMatch}</dd>
                            </>
                        )}
                    </dl>
                </section>
            )}
            {hasRemedies && (
                <section className="sol-section sol-correct-llm-answer">
                    <h3 className="sol-section-title">Diagnosis and Learning Moments</h3>
                    {working && (
                        <dl className="admin-answers">
                            <dt>Working</dt>
                            <dd>
                                {working.role === 'marked' ? 'marked' : 'not marked'}
                                {' '}({WORKING_SOURCE_LABEL[working.source] ?? working.source})
                            </dd>
                        </dl>
                    )}
                    {hasDiagnosis ? (
                        <AdminDiagnosisFields diagnosis={d!} />
                    ) : (
                        <p className="admin-empty-note">
                            The analyser named no cause for this attempt.
                        </p>
                    )}
                    {secondaries.length > 0 && (
                        <>
                            <h4 className="admin-sub-title">Secondary findings ({secondaries.length})</h4>
                            {secondaries.map((sd, i) => <AdminDiagnosisFields key={i} diagnosis={sd} />)}
                        </>
                    )}
                    {withheld.length > 0 && (
                        <>
                            <h4 className="admin-sub-title">Not reported — working not marked</h4>
                            <ul className="admin-withheld-list">
                                {withheld.map((w, i) => (
                                    <li key={i}>
                                        {w.kind ? w.kind.replace(/_/g, ' ') : 'finding'}
                                        {w.aspect ? ` — ${w.aspect.replace(/_/g, ' ')}` : ''}
                                        {w.event && <>: <LatexText text={w.event} /></>}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    <h4 className="admin-sub-title">
                        {/* The count is of moments that still STAND. A retracted
                            one is listed below but is no longer a claim about
                            this student, and counting it would say otherwise. */}
                        Learning moments{standing.length > 0 ? ` (${standing.length})` : ''}
                        {withdrawnCount > 0 && (
                            <span className="admin-moment-retracted-note">
                                {' '}· {withdrawnCount} retracted
                            </span>
                        )}
                    </h4>
                    {moments.length > 0 ? (
                        <ul className="admin-moment-list">
                            {moments.map((m, i) => (
                                <li
                                    key={`${m.learningMomentId}-${i}`}
                                    className={m.withdrawn ? 'admin-moment--withdrawn' : undefined}
                                >
                                    {/* The retraction replaces the applied/missed
                                        flag rather than sitting beside it: those
                                        describe a finding that counts, and this
                                        one does not. */}
                                    {m.withdrawn ? (
                                        <span
                                            className="admin-moment-flag admin-moment-flag--withdrawn"
                                            title={`Withdrawn (${m.withdrawn.reason}) on ${new Date(m.withdrawn.at).toLocaleString()}`}
                                        >
                                            {m.withdrawn.reason === 'disputed' ? 'retracted' : m.withdrawn.reason}
                                        </span>
                                    ) : (
                                        <span className={`admin-moment-flag admin-moment-flag--${m.applied ? 'applied' : 'missed'}`}>
                                            {m.applied ? 'applied' : 'missed'}
                                        </span>
                                    )}
                                    <span className="admin-moment-name">{m.name}</span>
                                    {m.context && <span className="admin-moment-context">{m.context}</span>}
                                    {m.withdrawn?.reason === 'rejected' && (
                                        <span className="admin-moment-rejection">{rejectionNote(m.withdrawn)}</span>
                                    )}
                                    {onRejectMoment && (
                                        <MomentRejectControl
                                            withdrawn={m.withdrawn}
                                            onSubmit={(rejected, comment, why) => onRejectMoment(m.learningMomentId, rejected, comment, why)}
                                        />
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        // Said out loud rather than left blank: "the detector
                        // found nothing here" and "the detector never ran" look
                        // identical as an absence, and an admin reading this page
                        // is usually trying to tell those apart.
                        <p className="admin-empty-note">No learning moments were detected on this submission.</p>
                    )}
                </section>
            )}
            {readers && (
                <section className="sol-section sol-correct-llm-answer">
                    <h3 className="sol-section-title">Reader Consistency</h3>
                    {readersCompared ? (
                        <dl className="admin-answers">
                            {/* The two values the verdict was reached from. Shown above it
                                because a verdict that does not follow from them is the
                                failure worth spotting, and an empty quote — the reading
                                reached no answer — is invisible in the verdict alone. */}
                            {(readers.answer.answerInExtractedWork || readers.answer.answerInEarlierReading) && (
                                <>
                                    <dt>Extracted work read</dt>
                                    <dd>{readers.answer.answerInExtractedWork
                                        ? <LatexText text={readers.answer.answerInExtractedWork} />
                                        : <span className="admin-empty-note">no final answer in this reading</span>}</dd>
                                    <dt>Earlier reading read</dt>
                                    <dd>{readers.answer.answerInEarlierReading
                                        ? <LatexText text={readers.answer.answerInEarlierReading} />
                                        : <span className="admin-empty-note">no final answer in this reading</span>}</dd>
                                </>
                            )}
                            <ReaderRow label="Final answer" judgement={readers.answer} />
                            <ReaderRow label="Working" judgement={readers.steps} />
                            {/* Step numbers, so they can be found in Steps below.
                                The two lists are disjoint: parted on is the readers
                                against each other, in neither is the analyser
                                against both — working it supplied itself. Absent on
                                rows analysed before the prompt asked. */}
                            {!!readers.steps.differingSteps?.length && (
                                <>
                                    <dt>Steps the readings parted on</dt>
                                    <dd>{readers.steps.differingSteps.join(', ')}</dd>
                                </>
                            )}
                            {!!readers.steps.stepsInNeitherReading?.length && (
                                <>
                                    <dt>Steps in neither reading</dt>
                                    <dd>{readers.steps.stepsInNeitherReading.join(', ')}</dd>
                                </>
                            )}
                            {/* On an MCQ, the options each reading names and the ones
                                the working reaches. When the readings name different
                                options, the one the working reaches is the reading
                                the page supports — so these sit just above what the
                                analyser actually settled on. */}
                            {[readers.answer.optionsInExtractedWork, readers.answer.optionsInEarlierReading,
                                readers.answer.optionsReachedByWorking, readers.answer.optionsSeenOnImage].some(l => l?.length) && (
                                <>
                                    <dt>Options named</dt>
                                    <dd>
                                        extracted work: {optionList(readers.answer.optionsInExtractedWork)}
                                        {' · '}earlier reading: {optionList(readers.answer.optionsInEarlierReading)}
                                        {' · '}working reaches: {optionList(readers.answer.optionsReachedByWorking)}
                                        {readers.answer.optionsSeenOnImage?.length ? <>{' · '}seen on image: {optionList(readers.answer.optionsSeenOnImage)}</> : null}
                                    </dd>
                                </>
                            )}
                            {/* What settled it. `image` is what the prompt asks for;
                                `unresolved` is an honest answer, not a failure. */}
                            {readers.answer.resolvedFrom && readers.answer.resolvedFrom !== 'not_applicable' && (
                                <>
                                    <dt>Settled from</dt>
                                    <dd>{readers.answer.resolvedFrom.replace(/_/g, ' ')}</dd>
                                </>
                            )}
                        </dl>
                    ) : (
                        <p className="admin-empty-note">
                            Only one reading of the page was available, so there was nothing to compare.
                        </p>
                    )}
                </section>
            )}
            {confidence && (
                <section className="sol-section sol-correct-llm-answer">
                    <h3 className="sol-section-title">Confidence</h3>
                    <p><span className={`confidence-badge ${confidence.band}`}>{confidence.text}</span></p>
                </section>
            )}
        </>
    );
}

/** An option list as it reads in a sentence; "none" for an empty one. */
/**
 * Every field of one diagnosis, as stored — vocabulary 2 or 1
 * (docs/FeatureDesigns/diagnosis/accuracy/diagnosis-structure-target.md).
 * Admins see everything, tentative included: they are the ones checking it.
 */
function AdminDiagnosisFields({ diagnosis: d }: { diagnosis: Diagnosis }) {
    const words = (v: string) => v.replace(/_/g, ' ');
    const v2 = d.vocabularyVersion === 2;
    return (
        <dl className="admin-answers">
            {d.kind && (<><dt>Kind</dt><dd>{d.kind === 'none' ? 'none (nothing to note)' : words(d.kind)}{d.aspect ? ` — ${words(d.aspect)}` : ''}</dd></>)}
            {d.stage && (<><dt>Stage</dt><dd>{words(d.stage)}</dd></>)}
            {d.stepRef && (<><dt>Rests on</dt><dd>{d.stepRef === 'answer' ? 'the answer' : `step ${d.stepRef.slice(1)}`} <code>{d.stepRef}</code></dd></>)}
            {d.event && (<><dt>Event</dt><dd><LatexText text={d.event} /></dd></>)}
            {d.cause && (<><dt>Cause</dt><dd><LatexText text={d.cause} /></dd></>)}
            {d.certainty && (<><dt>Certainty</dt><dd>{d.certainty === 'tentative' ? 'tentative — another cause is also plausible; not shown to the student' : 'clear'}</dd></>)}
            {(d.conceptName || d.concept) && (<><dt>Concept</dt><dd>{d.conceptName ?? d.concept}</dd></>)}
            {/* The id, not just the label: it is what patterns key on, so an
                admin checking why one did or did not accrue needs to see which
                one was chosen. */}
            {d.conceptId && (<><dt>Concept id</dt><dd><code>{d.conceptId}</code></dd></>)}
            {d.category && (<><dt>Category</dt><dd>{d.category}{v2 ? ' (derived)' : ''}</dd></>)}
            {!v2 && d.knowledgeType && (<><dt>Knowledge type</dt><dd>{d.knowledgeType}</dd></>)}
            {!v2 && typeof d.confidence === 'number' && (<><dt>Confidence</dt><dd>{d.confidence}</dd></>)}
            <dt>Vocabulary</dt><dd>{v2 ? 'v2' : 'v1'}</dd>
        </dl>
    );
}

function optionList(labels: readonly string[] | undefined): string {
    return labels?.length ? labels.join(', ') : 'none';
}
